import type { ReactNode } from 'react';
import type { DataSection } from '../../shared/api/contract.ts';
import type { DataKind } from '../../shared/freshness/index.ts';
import { useNow } from '../hooks/useNow.ts';
import { formatTime } from '../lib/format.ts';
import { DemoBadge, FreshnessBadge } from './FreshnessBadge.tsx';
import { sectionFreshness, unavailableMessage } from './freshness.ts';
import { Skeleton } from './Skeleton.tsx';
import { SourceLine } from './SourceLine.tsx';

interface SectionCardProps<T> {
  readonly title: string;
  readonly icon: ReactNode;
  readonly kind: DataKind;
  readonly section: DataSection<T> | undefined;
  readonly loading: boolean;
  /** Erreur de l'appel à NOTRE API (réseau, hors ligne) — distincte d'une panne fournisseur. */
  readonly fetchError?: Error | null;
  readonly headingLevel?: 2 | 3;
  /** Action d'en-tête (« Voir tous → »). */
  readonly actions?: ReactNode;
  /** Ce qui manque, dit en clair : « Données transport temporairement indisponibles. » */
  readonly subject?: string;
  /** Toujours affiché, même en panne (ex. choix de la gare : on doit pouvoir changer de gare si l'une est indisponible). */
  readonly toolbar?: ReactNode;
  /** Squelette propre au module (par défaut : lignes de texte). */
  readonly skeleton?: ReactNode;
  readonly className?: string | undefined;
  readonly children: (data: T, now: Date) => ReactNode;
}

/**
 * Bloc de données autonome : titre, contenu, état (chargement, indisponible, ancien, démo), provenance.
 * Une panne n'affecte que ce bloc (docs/04 §4). Aucun badge quand tout va bien : seuls « Données anciennes »
 * et « Démo » sont signalés dans l'en-tête.
 */
export function SectionCard<T>({
  title,
  icon,
  kind,
  section,
  loading,
  fetchError = null,
  headingLevel = 2,
  actions,
  subject,
  toolbar,
  skeleton,
  className,
  children,
}: SectionCardProps<T>) {
  const now = useNow(kind === 'departures' ? 15_000 : 60_000);
  const status = sectionFreshness(section, kind, now);
  const Heading = headingLevel === 2 ? 'h2' : 'h3';
  const titleId = `section-${kind}-${title.replace(/\W+/g, '-').toLowerCase()}`;

  let body: ReactNode;
  if (section === undefined) {
    body =
      loading && fetchError === null ? (
        <div aria-hidden="true">{skeleton ?? <Skeleton lines={3} />}</div>
      ) : (
        <p className="state-message">{unavailableMessage(undefined, subject)}</p>
      );
  } else if (status === 'unavailable' || section.data === null) {
    body = <p className="state-message">{unavailableMessage(section, subject)}</p>;
  } else {
    const updatedAt = section.meta.observedAt ?? section.meta.fetchedAt;
    body = (
      <div className="card__content">
        {children(section.data, now)}
        {status === 'stale' && updatedAt !== null && (
          <p className="stale-warning" role="note">
            Dernière mise à jour connue : {formatTime(updatedAt)}, peut être obsolète.
          </p>
        )}
      </div>
    );
  }

  return (
    <section
      className={`card${className === undefined ? '' : ` ${className}`}`}
      aria-labelledby={titleId}
      aria-busy={loading && section === undefined}
    >
      <div className="card__header">
        <Heading className="card__title" id={titleId}>
          {icon}
          {title}
        </Heading>
        {section?.demo === true && <DemoBadge />}
        {section !== undefined && status === 'stale' && <FreshnessBadge status={status} />}
        {actions !== undefined && <div className="card__actions">{actions}</div>}
      </div>
      {toolbar}
      {body}
      {section !== undefined && <SourceLine section={section} now={now} kind={kind} />}
    </section>
  );
}
